import styled from '@emotion/styled';
import { PrimaryButton, TextInputField } from '@liveoptics/react-ui';
import { useRequest } from 'ahooks';
import React, { FormEvent, useCallback, useState } from 'react';
import { registerNewUser, verifyEndUser } from '../../../../api/api';
import {
  Error,
  OpportunityMode,
  TheaterType,
  TheaterTypeIndex,
  TheaterTypeIndexMapper,
  VerifyOwnerState,
} from '../../../../enums';
import { Country, RegisterNewUserRequest, SalesforceAccount } from '../../../../interfaces';
import TextButton from '../../../buttons/TextButton';
import { SalesforceAccountsAutoComplete } from '../../../form-fields';
import { CountriesAutoComplete } from '../../../form-fields/auto-complete/CountriesAutoComplete';
import { TheatersDropDownMenu } from '../../../menus';
import SelectionOption from '../SelectionOption';
import Form from '../form/Form';

const CustomCountriesAutoComplete = styled(CountriesAutoComplete)<{ open: boolean }>`
  margin-bottom: ${(p) => (p.open ? '201px' : '1px')};
`;

const NewCustomerForm = (props: {
  projectId: number;
  email: string;
  defaultTheater: TheaterType;
  registerNewUserRequest?: RegisterNewUserRequest;
  verifyOwnerStateCallback: (state: VerifyOwnerState, value?: unknown) => void;
  onEmailChange: (e: FormEvent<HTMLInputElement>) => void;
  onOptionChange: (option: SelectionOption) => void;
  onCancel: () => void;
  onSuccess: (state: VerifyOwnerState, email?: string) => void;
}) => {
  const prevSfdcAccountId: SalesforceAccount | undefined =
    props.registerNewUserRequest?.CompanySFAccountId && props.registerNewUserRequest?.CompanyName
      ? {
          SFAccountID: props.registerNewUserRequest.CompanySFAccountId,
          Name: props.registerNewUserRequest.CompanyName,
        }
      : undefined;
  const [theater, setTheater] = useState<TheaterType>(
    props.registerNewUserRequest?.Theater
      ? TheaterTypeIndexMapper[props.registerNewUserRequest?.Theater]
      : props.defaultTheater
  );
  const [account, setAccount] = useState<SalesforceAccount | undefined>(prevSfdcAccountId);
  const [firstName, setFirstName] = useState(props.registerNewUserRequest?.FirstName ?? '');
  const [lastName, setLastName] = useState(props.registerNewUserRequest?.LastName ?? '');
  const [country, setCountry] = useState<Country | undefined>(
    props.registerNewUserRequest?.CountryId && props.registerNewUserRequest.CountryString
      ? {
          Id: props.registerNewUserRequest.CountryId,
          Name: props.registerNewUserRequest.CountryString,
        }
      : undefined
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countriesDropDownOpen, setCountriesDropDownOpen] = useState(false);

  const registerNewUserRequest = useRequest(registerNewUser, {
    manual: true,
    debounceWait: 500,
  });

  const verifyEndUserRequest = useRequest(verifyEndUser, {
    manual: true,
    debounceWait: 500,
  });

  const constructRegisterNewUserRequestObject = useCallback(
    (): RegisterNewUserRequest => ({
      CountryId: country?.Id ? country?.Id : 0,
      Email: props.email,
      FirstName: firstName,
      LastName: lastName,
      Theater: TheaterTypeIndex[theater.toString()],
      CompanyName: account?.Name ?? '',
      CompanySFAccountId: account?.SFAccountID ?? '',
    }),
    [account?.Name, account?.SFAccountID, country?.Id, firstName, lastName, props.email, theater]
  );

  const handleTheaterChange = useCallback(
    (value: string) => {
      setError('');
      setTheater(TheaterType[value]);
    },
    [props]
  );

  const handleFirstNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setError('');
      setFirstName(e.currentTarget.value);
    },
    [props]
  );

  const handleLastNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setError('');
      setLastName(e.currentTarget.value);
    },
    [props]
  );

  const handleCountryChange = useCallback(
    (c: Country) => {
      setError('');
      setCountry(c);
    },
    [props]
  );

  const handleAccountChange = useCallback(
    (a: SalesforceAccount) => {
      setError('');
      setAccount(a);
    },
    [props]
  );

  const handleAccountValueChange = useCallback(
    (value: string) => {
      setError('');
      setAccount({ SFAccountID: '', Name: value });
    },
    [props]
  );

  const handleVerifyOwnerStateChange = useCallback(
    () =>
      props.verifyOwnerStateCallback(VerifyOwnerState.SetOpportunity, {
        ...constructRegisterNewUserRequestObject(),
        CountryString: country?.Name,
      }),
    [constructRegisterNewUserRequestObject, country?.Name, props]
  );

  const handleSaveClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      setError('');
      setLoading(true);
      registerNewUserRequest
        .runAsync(constructRegisterNewUserRequestObject())
        .then((user) => {
          verifyEndUserRequest
            .runAsync(props.projectId, {
              mode: OpportunityMode.None,
              ownerAccountId: user.Data.Id,
            })
            .then(() => {
              props.onSuccess(VerifyOwnerState.VerifyEndUser, props.email);
              setLoading(false);
            })
            .catch(() => {
              setLoading(false);
              setError(Error.VerifyEndUser);
            });
        })
        .catch(() => {
          setLoading(false);
          setError(Error.RegisterNewUser);
        });
    },
    [props, registerNewUserRequest, constructRegisterNewUserRequestObject, verifyEndUserRequest]
  );

  const defaultSalesforceAccountsAutoComplete =
    props.registerNewUserRequest?.CompanyName && props.registerNewUserRequest.CompanySFAccountId
      ? `${props.registerNewUserRequest.CompanyName} - ${props.registerNewUserRequest.CompanySFAccountId}`
      : '';

  return (
    <Form
      selectionOption={SelectionOption.Customer}
      error={error}
      renderActions={() => (
        <>
          <TextButton onClick={props.onCancel}>Cancel</TextButton>
          {account?.Name && account?.SFAccountID ? (
            <PrimaryButton
              clickFn={handleVerifyOwnerStateChange}
              disabled={
                !(firstName && lastName && country?.Name && account?.Name && account?.SFAccountID)
              }
            >
              Link SFDC ID
            </PrimaryButton>
          ) : (
            <PrimaryButton
              loading={loading}
              loadingText="Loading..."
              disabled={!(firstName && lastName && country?.Name) || loading}
              clickFn={handleSaveClick}
            >
              Save
            </PrimaryButton>
          )}
        </>
      )}
      onChange={props.onOptionChange}
    >
      <TextInputField
        label=""
        autoFocus={true}
        autoComplete="on"
        type="email"
        placeholder="Enter email address"
        value={props.email}
        status="active"
        validationMessage={'Please enter a valid customer email address'}
        onChange={props.onEmailChange}
      />
      <div>
        <TheatersDropDownMenu defaultValue={TheaterType[theater]} onChange={handleTheaterChange} />
      </div>
      <SalesforceAccountsAutoComplete
        theater={theater}
        placeholder="Account Name / SFDC ID (Optional)"
        defaultValue={defaultSalesforceAccountsAutoComplete}
        onAccountChange={handleAccountChange}
        onAccountValueChange={handleAccountValueChange}
      />
      <TextInputField
        label=""
        placeholder="First Name"
        defaultValue={firstName}
        status="active"
        onChange={handleFirstNameChange}
      />
      <TextInputField
        label=""
        placeholder="Last Name"
        status="active"
        defaultValue={lastName}
        onChange={handleLastNameChange}
      />
      <CustomCountriesAutoComplete
        open={countriesDropDownOpen}
        placeholder="Country"
        defaultValue={country?.Name}
        onCountryChange={handleCountryChange}
        onDropDownToggle={(show) => setCountriesDropDownOpen(show)}
      />
    </Form>
  );
};

export default NewCustomerForm;
